/* The stage registry is the ONE vocabulary (docs/CONCEPTS.md "Stufe").
   The server stores it as `events.stages`; the dashboard used to keep six
   hardcoded copies (advFilters.js STAGES, a second STAGES in Settings.js,
   the --stage-* tokens, the filter chips, the name<->key maps). Every one of
   them now reads the registry through this hook, so a custom per-event stage
   (name + colour + rules) shows up everywhere the built-in seven do.

   deriveRegistry is a client mirror of the backend `readStageRegistry`
   (src/server/stageRegistry.js): a saved `stages` array wins, otherwise the
   legacy `stageConfig` map is normalised into the built-in seven plus the
   map's own keys (R36). Keep the two in sync — the AE7 no-drift guard proves
   the server pipeline and this mirror agree. */

import { useMemo } from 'react';

/* The seven built-in stages, seeded exactly as the backend does: array order
   is rule-priority order, leiter/helfer carry both flags, colour is a
   `--ak-*` token name (never a hex) so contrast stays a CSS decision. */
const BUILTIN_STAGES = [
  { key: 'biber',      name: 'Biber',       colour: 'ak-biber',      flags: { leiter: false, zwei2de: false }, rules: [] },
  { key: 'woelfling',  name: 'Wölfling',    colour: 'ak-woe',        flags: { leiter: false, zwei2de: false }, rules: [] },
  { key: 'jupfi',      name: 'Jupfi',       colour: 'ak-jupfi',      flags: { leiter: false, zwei2de: false }, rules: [] },
  { key: 'pfadi',      name: 'Pfadi',       colour: 'ak-pfadi',      flags: { leiter: false, zwei2de: false }, rules: [] },
  { key: 'rover',      name: 'Rover',       colour: 'ak-rover',      flags: { leiter: false, zwei2de: false }, rules: [] },
  { key: 'leiter',     name: 'Leitende*r',  colour: 'ak-leitende',   flags: { leiter: true,  zwei2de: true  }, rules: [] },
  { key: 'helfer',     name: 'Helfende*r',  colour: 'ak-ausbildung', flags: { leiter: true,  zwei2de: true  }, rules: [] },
];

/* The seven built-in keys: the editor marks a row as built-in and locks its
   key (R2: fixed once created) from this single source, never a fresh copy. */
export const BUILTIN_KEYS = BUILTIN_STAGES.map((s) => s.key);

/* The rule-less bucket of the birth-date fallback. It is NOT a registry
   entry (the server refuses a stage named that way) but it IS a display
   name, so the vocabulary keeps it for rendering a ticket that has no
   birthdate and matched no rule. */
export const UNBEKANNT_KEY = 'unbekannt';
export const UNBEKANNT_NAME = 'Unbekannt';

const clone = (s) => ({
  key: s.key,
  name: s.name,
  colour: s.colour ?? undefined,
  flags: { leiter: !!s.flags?.leiter, zwei2de: !!s.flags?.zwei2de },
  rules: Array.isArray(s.rules) ? s.rules.map((r) => ({ ...r })) : [],
  scope: s.scope ?? undefined,
  legacy: s.legacy ?? undefined,
});

/* Pure mirror of the backend readStageRegistry(evt). `ev` is the event the
   caller selected (getEvents returns full documents, so a saved `stages`
   array is present; a legacy event carries only `stageConfig`). */
export function deriveRegistry(ev) {
  if (Array.isArray(ev?.stages) && ev.stages.length > 0) {
    return ev.stages.map(clone);
  }

  const legacy = ev?.stageConfig;
  if (legacy && typeof legacy === 'object' && !Array.isArray(legacy)) {
    const hasRules = Object.values(legacy).some((a) => Array.isArray(a) && a.length > 0);
    if (hasRules) {
      const registry = BUILTIN_STAGES.map((b) => ({
        ...clone(b),
        legacy: Array.isArray(legacy[b.key]) ? true : undefined,
        rules: Array.isArray(legacy[b.key]) ? legacy[b.key].map((r) => ({ ...r })) : [],
      }));
      for (const [key, arr] of Object.entries(legacy)) {
        if (BUILTIN_STAGES.some((s) => s.key === key)) continue;
        if (!Array.isArray(arr)) continue;
        registry.push({
          key,
          name: key,
          colour: undefined,
          flags: { leiter: false, zwei2de: false },
          legacy: true,
          rules: arr.map((r) => ({ ...r })),
          scope: undefined,
        });
      }
      return registry;
    }
  }

  return BUILTIN_STAGES.map(clone);
}

/* key -> display name. The registry first, then the rule-less fallback
   bucket, then the key itself (an unknown stage passes through, exactly as
   the old STAGES map did). */
export function makeKeyToName(registry) {
  const byKey = new Map(registry.map((s) => [s.key, s.name]));
  return (val) => {
    if (val == null) return val;
    if (val === UNBEKANNT_KEY) return UNBEKANNT_NAME;
    return byKey.get(val) ?? val;
  };
}

/* display name -> key. Only exact registry names resolve; 'Unbekannt' maps
   back to the fallback bucket. Anything else is null (the old map returned
   null for an unknown name too). */
export function makeNameToKey(registry) {
  const byName = new Map(registry.map((s) => [s.name, s.key]));
  return (name) => {
    if (byName.has(name)) return byName.get(name);
    if (name === UNBEKANNT_NAME) return UNBEKANNT_KEY;
    return null;
  };
}

/* The one hook. Returns the registry plus the three vocabularies the whole
   dashboard used to hardcode. `STAGES` keeps the old shape ({ key: { name, … } })
   so consumers that iterate it (the filter chips, the editor) keep working;
   `registry` is the ordered array for anything that needs order/colour/flags. */
export function useStageRegistry(ev) {
  // Der Event-Wechsel ist pro Auswahl stabil; nur `stages` und `stageConfig`
  // ändern das Vokabular. Auf die Objektidentität von `ev` zu reagieren würde
  // bei jedem Refetch neu ableiten und die schwerste Query erneut auslösen
  // (AGENTS Refetch-Loop) — darum die zwei Felder, nicht `ev` selbst.
  const stages = ev?.stages;
  const stageConfig = ev?.stageConfig;
  const registry = useMemo(() => deriveRegistry({ stages, stageConfig }), [
    stages,
    stageConfig,
  ]);
  const stageKeyToName = useMemo(() => makeKeyToName(registry), [registry]);
  const nameToStageKey = useMemo(() => makeNameToKey(registry), [registry]);
  const STAGES = useMemo(
    () => Object.fromEntries(registry.map((s) => [s.key, { name: s.name, colour: s.colour, flags: s.flags }])),
    [registry],
  );
  return { registry, STAGES, stageKeyToName, nameToStageKey };
}
